import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../../Components/Ui/Card';
import Button from '../../../Components/Ui/Button';
import { ArrowLeft } from 'lucide-react';
import { useGetAllCategoriesQuery } from '../../../app/services/productApi';
// import LoadingSpinner from "../../../Components/Ui/LoadingSpinner";
import { toast } from 'react-toastify';

const CatalogDetails = () => {
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState('');

  const { data: catalogItem, isLoading, isError, error } = useGetAllCategoriesQuery();

  useEffect(() => {
    if (catalogItem?.productDetails?.images?.[0]) {
      setSelectedImage(catalogItem.productDetails.images[0]);
    }
  }, [catalogItem]);

  if (isLoading) {
    return <div className="flex justify-center items-center h-64">...Loading</div>;
  }

  if (isError) {
    toast.error(error?.data?.message || 'Failed to load product details');
    return (
      <Card className="p-6 text-center">
        <h2 className="text-xl font-bold text-red-600">Error Loading Product</h2>
        <Button onClick={() => navigate(-1)} className="mt-4">
          Back to Catalog
        </Button>
      </Card>
    );
  }

  if (!catalogItem) {
    return (
      <Card className="p-6 text-center">
        <h2 className="text-xl font-bold text-red-600">Product Not Found</h2>
        <Button onClick={() => navigate(-1)} className="mt-4">
          Back to Catalog
        </Button>
      </Card>
    );
  }

  const productImages = catalogItem.productDetails?.images?.length
    ? catalogItem.productDetails.images
    : ['https://via.placeholder.com/500x500?text=No+Image+Available'];

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Product Details</h2>
        <Button onClick={() => navigate(-1)} className="flex items-center gap-1">
          <ArrowLeft size={16} />
          Back
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-10">
        <div className="bg-gray-100 rounded-3xl p-8 flex flex-col items-center">
          <div className="relative w-full aspect-square max-w-lg group mb-4">
            <img
              src={selectedImage || productImages[0]}
              alt={catalogItem.productDetails?.title}
              className="object-contain w-full h-full rounded-2xl shadow-lg transition-transform duration-300 group-hover:scale-105"
            />
            <div className="absolute bottom-0 left-0 right-0 z-20 bg-black/60 white-text text-center py-2 text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              {catalogItem.productDetails?.title}
            </div>
          </div>

          <div className="flex gap-3 flex-wrap justify-center">
            {productImages.map((img, index) => (
              <img
                key={index}
                src={img}
                alt={`thumb-${index}`}
                onClick={() => setSelectedImage(img)}
                className={`w-20 h-20 object-cover rounded-xl border-2 cursor-pointer ${
                  selectedImage === img ? 'border-blue-500' : 'border-gray-300'
                }`}
              />
            ))}
          </div>
        </div>

        <div className="space-y-6">
          <h3 className="text-3xl font-semibold text-gray-900">
            {catalogItem.productDetails?.title}
          </h3>
          <p className="text-secondary text-md">
            <span className="font-medium text-gray-600">Brand:</span>{' '}
            {catalogItem.brandDetails?.name || 'N/A'}
          </p>
          <p className="text-secondary text-md">
            <span className="font-medium text-gray-600">Category:</span>{' '}
            {catalogItem.categoryDetails?.name || 'N/A'}
          </p>

          <div className="grid grid-cols-2 gap-4 text-md">
            <p>
              <strong>Your Price:</strong> ₹{catalogItem.sellerPrice?.toFixed(2)}
            </p>
            <p>
              <strong>MRP:</strong> ₹{catalogItem.productDetails?.mrp?.toFixed(2) || 'N/A'}
            </p>
            <p>
              <strong>Discount:</strong> {catalogItem.discountPercentage || 0}%
            </p>
            <p>
              <strong>MOQ:</strong> {catalogItem.moq || 1}
            </p>
            <p>
              <strong>Stock:</strong> {catalogItem.stock || 0}
            </p>
            <p>
              <strong>Status:</strong>{' '}
              <span
                className={`font-semibold ${
                  catalogItem.status === 'active' ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {catalogItem.status}
              </span>
            </p>
          </div>

          <div className="pt-4">
            <h4 className="font-medium text-gray-600 mb-2">Description:</h4>
            <p className="text-secondary">
              {catalogItem.productDetails?.description || 'No description available'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CatalogDetails;
